import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { refreshAdminNotifications } from "@/components/NotificationBell";
import { formatBatchToast } from "@/pages/admin/SalesBulkBar";

const REASONS = ["잘못 생성", "중복 생성", "테스트 데이터", "고객 요청", "계약 취소", "기타"];
const TYPE_LABEL = { quote: "견적", contract: "계약", consultation: "상담" };
const PATH = {
  quote: "/admin/batch-delete/quotes",
  contract: "/admin/batch-delete/contracts",
  consultation: "/admin/batch-delete/consultations",
};

function userFacingBatchError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const text = typeof detail === "string" ? detail : "";
  const blob = `${status || ""} ${text} ${err?.message || ""}`;
  if (status === 405 || /method not allowed|\b405\b|api exception/i.test(blob)) {
    return "삭제를 처리하지 못했습니다. 페이지를 새로고침한 뒤 다시 시도해 주세요.";
  }
  if (text && !/exception|traceback|not allowed|internal server/i.test(text)) {
    return text;
  }
  return "삭제를 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";
}

export default function BatchDeleteModal({ resourceType, ids, isOwner, onClose, onDone }) {
  const [reason, setReason] = useState("테스트 데이터");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const noun = TYPE_LABEL[resourceType] || resourceType;
  const count = (ids || []).length;

  const submit = async () => {
    if (!count) return;
    if (!isOwner && reason === "기타" && !detail.trim()) {
      return toast.error("기타 사유를 입력해 주세요.");
    }
    setBusy(true);
    try {
      const r = await api.post(PATH[resourceType], {
        ids,
        reason: isOwner ? undefined : reason,
        reason_detail: isOwner ? undefined : detail,
      });
      refreshAdminNotifications();
      const data = r.data || {};
      const msg = formatBatchToast(noun, data);
      if ((data.failed_count || 0) > 0) toast.error(msg);
      else toast.success(msg);
      onDone?.(data);
    } catch (e) {
      toast.error(userFacingBatchError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="sales-batch-delete-modal">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 w-full max-w-md space-y-3">
        <h4 className="font-medium">선택한 {noun} {count}건을 {isOwner ? "삭제" : "삭제 요청"}하시겠습니까?</h4>
        {isOwner ? (
          <p className="text-sm text-red-300">삭제 후 복구할 수 없는 항목이 포함될 수 있습니다. 연결된 견적·계약이 있으면 해당 건은 삭제되지 않습니다.</p>
        ) : (
          <>
            <p className="text-sm text-white/70">OWNER 승인 전까지 원본은 유지됩니다. 사유는 선택한 모든 항목에 동일하게 적용됩니다.</p>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm">
              {REASONS.map((row) => <option key={row}>{row}</option>)}
            </select>
            <textarea
              rows={3}
              placeholder={reason === "기타" ? "상세 사유 (필수)" : "상세 사유 (선택)"}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
            />
          </>
        )}
        <div className="flex gap-2">
          <button type="button" disabled={busy} onClick={onClose} className="border border-white/10 rounded-lg px-3 py-2 text-sm">취소</button>
          <button
            type="button"
            disabled={busy}
            onClick={submit}
            className="bg-red-500 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50"
            data-testid="sales-batch-delete-submit"
          >
            {busy ? "처리 중..." : (isOwner ? `${count}건 삭제` : `${count}건 삭제 요청`)}
          </button>
        </div>
      </div>
    </div>
  );
}
